
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { Image } from "@/lib/types";
import { useAuth } from "@/contexts/AuthContext";

interface StatsData {
  totalViews: number;
  totalImages: number;
  mostViewedImage: Image | null;
  viewsByImage: {
    name: string;
    views: number;
  }[];
}

const Stats = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<StatsData>({
    totalViews: 0,
    totalImages: 0,
    mostViewedImage: null,
    viewsByImage: [],
  });
  const { session } = useAuth();

  useEffect(() => {
    if (!session?.user) return;

    const fetchStats = async () => {
      try {
        // Fetch all user images
        const { data: images, error } = await supabase
          .from('images')
          .select('*')
          .eq('user_id', session.user.id);

        if (error) {
          console.error('Error fetching images:', error);
          return;
        }

        if (!images || images.length === 0) {
          setLoading(false);
          return;
        }

        // Get public URLs for all images
        const imagesWithUrls = images.map(img => {
          const { data: { publicUrl } } = supabase.storage
            .from('images')
            .getPublicUrl(img.storage_path);

          return {
            id: img.id,
            name: img.name,
            description: img.description || undefined,
            url: publicUrl,
            slug: img.slug,
            createdAt: new Date(img.created_at).getTime(),
            views: img.views
          };
        });

        // Calculate stats
        const totalViews = imagesWithUrls.reduce((sum, img) => sum + img.views, 0);
        const totalImages = imagesWithUrls.length;
        const mostViewedImage = imagesWithUrls.reduce((prev, current) => 
          (prev.views > current.views) ? prev : current, imagesWithUrls[0]);

        // Create data for chart
        const viewsByImage = imagesWithUrls
          .sort((a, b) => b.views - a.views)
          .map(img => ({
            name: img.name.length > 20 ? img.name.substring(0, 17) + '...' : img.name,
            views: img.views
          }));

        setStats({
          totalViews,
          totalImages,
          mostViewedImage,
          viewsByImage,
        });

        setLoading(false);
      } catch (error) {
        console.error('Error calculating stats:', error);
        setLoading(false);
      }
    };

    fetchStats();
  }, [session]);

  if (loading) {
    return (
      <Card className="glass-card">
        <CardContent className="p-6">
          <p>Loading stats...</p>
        </CardContent>
      </Card>
    );
  }

  if (stats.totalImages === 0) {
    return (
      <Card className="glass-card">
        <CardContent className="p-6">
          <p>No images uploaded yet. Upload some images to see your stats!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="glass-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-white/70">Total Images</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalImages}</div>
          </CardContent>
        </Card>
        
        <Card className="glass-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-white/70">Total Views</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalViews}</div>
          </CardContent>
        </Card>
        
        <Card className="glass-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-white/70">Avg. Views per Image</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalImages > 0 ? Math.round(stats.totalViews / stats.totalImages) : 0}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {stats.mostViewedImage && (
        <Card className="glass-card">
          <CardHeader>
            <CardTitle>Most Viewed Image</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/3 aspect-video overflow-hidden rounded-md bg-black/20">
              <img 
                src={stats.mostViewedImage.url} 
                alt={stats.mostViewedImage.name} 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-medium mb-2">{stats.mostViewedImage.name}</h3>
              {stats.mostViewedImage.description && (
                <p className="text-white/70 mb-4">{stats.mostViewedImage.description}</p>
              )}
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-white/50 text-sm">Views</p>
                  <p className="text-2xl font-bold">{stats.mostViewedImage.views}</p>
                </div>
                <div>
                  <p className="text-white/50 text-sm">Uploaded</p>
                  <p className="text-sm">
                    {new Date(stats.mostViewedImage.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      <Card className="glass-card">
        <CardHeader>
          <CardTitle>Views by Image</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ChartContainer
              config={{
                views: {
                  theme: {
                    light: "#2ED573",
                    dark: "#2ED573"
                  },
                  label: "Views"
                }
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={stats.viewsByImage} 
                  margin={{ top: 5, right: 5, left: 5, bottom: 20 }}
                >
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={70} 
                    tick={{ fill: 'rgba(255, 255, 255, 0.7)', fontSize: 12 }}
                  />
                  <YAxis tick={{ fill: 'rgba(255, 255, 255, 0.7)' }} />
                  <Bar dataKey="views" fill="#2ED573" radius={[4, 4, 0, 0]} />
                  <ChartTooltip
                    content={<ChartTooltipContent />}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </div>
        </CardContent>
      </Card>
      
      <Card className="glass-card">
        <CardHeader>
          <CardTitle>Image Details</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Image Name</TableHead>
                <TableHead>Upload Date</TableHead>
                <TableHead className="text-right">Views</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.viewsByImage.map((image, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{image.name}</TableCell>
                  <TableCell>
                    {new Date(
                      stats.mostViewedImage && 
                      stats.viewsByImage[index].name === 
                        (stats.mostViewedImage.name.length > 20 
                          ? stats.mostViewedImage.name.substring(0, 17) + '...' 
                          : stats.mostViewedImage.name)
                      ? stats.mostViewedImage.createdAt
                      : Date.now()
                    ).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">{image.views}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Stats;
